import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { Button, Skeleton } from "../../../components/shared";
import {
  DraftFlowProvider,
  useDraftFlowData,
  useDraftFlowActions,
  getMessageRole,
} from "../../../components/AIChat/DraftFlowProvider";
import { isContractInProgress } from "../../../components/AIChat/contractStates";
import { useDraftFlowStore } from "../../../stores/draftFlowStore";
import { formatDateLabel, formatToReadableTime } from "../../../utils/utils";
import { cn } from "../../../lib/cn";
import { COLORS } from "../../../constants/colors";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { queryKeys } from "../../../redux/network/constant";
import {
  SendIcon,
  TriangleIcon,
  StopIcon,
  ChatIcon,
} from "../../../assets/icons";
import { ContractGenerateIcon } from "../../../assets/icons/ContractGenerate";
import { getMessageContent } from "./utils/messageUtils";
import {
  ContractReadyCard,
  ContractFailedCard,
  ContractExpiredCard,
} from "./components/ContractCards";
import { ChatMessageContent } from "./components/ChatMessageContent";
import { ContractPanel } from "./components/ContractPanel";
import { DraftFlowSidebar } from "./components/DraftFlowSidebar";
import { DraftFlowHeader } from "./components/DraftFlowHeader";
import { PermissionBar } from "./components/PermissionBar";
import { PermissionPicker } from "./components/PermissionPicker";
import { SaveActions } from "./components/SaveActions";

const DraftFlowContent = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { data, loading } = useDraftFlowData();
  const {
    handleProjectChange,
    handleCreateSession,
    handleSessionSelect,
    handleSendMessage: sendMessage,
    handleRetryMessage,
    handleStopGeneration,
    handleChatScroll,
    toggleSidebar,
    scrollRef,
    messageInputRef,
    creatingSession,
  } = useDraftFlowActions();
  const {
    selectedProjectId,
    activeSessionId,
    intentData,
    pendingAssistant,
    showSidebar,
    isProjectPopoverOpen,
    setIsProjectPopoverOpen,
    setContractId,
    setShowContractPanel,
    grant,
    summaryMeta,
    permissionPickerOpen,
    setPermissionPickerOpen,
    sandboxProgressByJobId,
    agentProgress,
  } = useDraftFlowStore();
  const {
    projectOptions,
    projectsById,
    sessions,
    displayMessages: messages,
    contracts,
    activeSession,
    contractDetail,
  } = data;
  const resolvedProjectName =
    projectOptions.find((p) => p.id === selectedProjectId)?.name ||
    projectsById?.[String(activeSession?.project_id)]?.title ||
    projectsById?.[String(activeSession?.project_id)]?.name;
  const activeProject =
    projectsById?.[String(activeSession?.project_id)] ||
    projectsById?.[String(selectedProjectId)] ||
    null;
  const activeTeamId =
    activeProject?.team_id ||
    activeProject?.team ||
    grant?.team ||
    null;
  const sessionCount = selectedProjectId
    ? sessions.filter(
        (session) => String(session?.project_id) === String(selectedProjectId),
      )
    : sessions;
  const {
    projectsLoading,
    sessionDetailLoading,
    messagesLoading,
    contractLoading,
  } = loading;
  const [messageInput, setMessageInput] = useState("");
  const [isSessionPopoverOpen, setIsSessionPopoverOpen] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const prevPendingAssistantRef = useRef(pendingAssistant);

  // Restore focus to input when assistant finishes responding
  useEffect(() => {
    if (prevPendingAssistantRef.current && !pendingAssistant) {
      messageInputRef.current?.focus();
    }
    prevPendingAssistantRef.current = pendingAssistant;
  }, [pendingAssistant, messageInputRef]);

  // Auto-select latest contract when contracts change
  useEffect(() => {
    if (contracts.length > 0 && !selectedContractId) {
      setSelectedContractId(contracts[0].id);
      setShowContractPanel(true);
    }
  }, [contracts, selectedContractId, setShowContractPanel]);

  // Update contractId in store when selection changes
  useEffect(() => {
    if (selectedContractId) {
      setContractId(selectedContractId);
    }
  }, [selectedContractId, setContractId]);

  const isDraftFlowRoute = useMemo(
    () => location.pathname.includes("/draft-flow"),
    [location.pathname],
  );
  const isStandaloneDraftFlow =
    isDraftFlowRoute && !location.pathname.includes("/projects/");
  // Collapse sidebar by default on draft flow to maximise chat space
  useEffect(() => {
    if (isDraftFlowRoute && showSidebar) {
      toggleSidebar();
    }
    // On mobile, don't auto-open the contract panel
    if (isMobile) {
      setShowContractPanel(false);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = useCallback(() => {
    sendMessage(messageInput);
    setMessageInput("");
  }, [messageInput, sendMessage]);

  const handleViewContract = useCallback((contractId, metadata) => {
    console.log("[ContractReadyCard] onView clicked", {
      contractId,
      activeSessionId,
      metadata,
    });
    if (contractId) {
      console.log("[ContractReadyCard] Setting selectedContractId:", contractId);
      setSelectedContractId(contractId);
      setContractId(contractId);
      console.log("[ContractReadyCard] Invalidating query for contract:", contractId);
      queryClient.invalidateQueries({
        queryKey: queryKeys.AIChatContract(contractId),
      });
    }
    if (activeSessionId) {
      console.log("[ContractReadyCard] Selecting session:", activeSessionId);
      handleSessionSelect(activeSessionId);
    } else {
      console.warn("[ContractReadyCard] No activeSessionId to select");
    }
    console.log("[ContractReadyCard] Opening contract panel");
    setShowContractPanel(true);
  }, [activeSessionId, handleSessionSelect, setContractId, setShowContractPanel, setSelectedContractId, queryClient]);

  const messageItems = useMemo(() => {
    const progressItems = Object.values(sandboxProgressByJobId || {}).map((progress) => ({
      type: "sandbox_progress",
      progress,
      key: `sandbox-progress-${progress.job_id}`,
    }));
    if (agentProgress && String(agentProgress.session_id) === String(activeSessionId)) {
      progressItems.push({ type: "agent_progress", progress: agentProgress, key: "agent-progress" });
    }
    if (!messages?.length) {
      return progressItems;
    }
    let lastLabel = null;
    return messages.flatMap((message, index) => {
      const createdAt = message?.created_at;
      const label = createdAt ? formatDateLabel(createdAt) : null;
      const nextItems = [];
      if (label && label !== lastLabel) {
        lastLabel = label;
        nextItems.push({
          type: "date",
          label,
          key: `date-${label}-${createdAt}`,
        });
      }
      const messageKeyBase =
        message?.client_message_id || message?.id || message?.request_id;
      const role = getMessageRole(message);
      nextItems.push({
        type: "message",
        message,
        key: messageKeyBase
          ? `message-${role}-${messageKeyBase}`
          : `message-idx-${index}`,
      });
      return nextItems;
    }).concat(progressItems);
  }, [messages, sandboxProgressByJobId, agentProgress, activeSessionId]);

  const isInputDisabled = Boolean(
    pendingAssistant || creatingSession,
  );

  return (
    <div className="h-full">
      <div className={cn("relative flex h-full bg-white overflow-hidden", isDraftFlowRoute && "px-4 md:px-6 py-4")}>
        {/* Sidebar */}
        {!isDraftFlowRoute && (
          <DraftFlowSidebar
            showSidebar={showSidebar}
            onToggleSidebar={toggleSidebar}
            selectedProjectId={selectedProjectId}
            projectOptions={projectOptions}
            projectsLoading={projectsLoading}
            onProjectChange={handleProjectChange}
            onCreateSession={handleCreateSession}
            creatingSession={creatingSession}
            onSessionSelect={handleSessionSelect}
            isProjectPopoverOpen={isProjectPopoverOpen}
            setIsProjectPopoverOpen={setIsProjectPopoverOpen}
          />
        )}

        {/* Chat Area */}
        <div className="flex flex-col h-full min-h-0 flex-1 min-w-0 overflow-hidden">
          <DraftFlowHeader
            isDraftFlowRoute={isDraftFlowRoute}
            isStandaloneDraftFlow={isStandaloneDraftFlow}
            resolvedProjectName={resolvedProjectName}
            projectOptions={projectOptions}
            selectedProjectId={selectedProjectId}
            projectsLoading={projectsLoading}
            onProjectChange={handleProjectChange}
            sessions={sessions}
            activeSession={activeSession}
            onSessionSelect={handleSessionSelect}
            onCreateSession={handleCreateSession}
            creatingSession={creatingSession}
            sessionCount={sessionCount}
            sessionDetailLoading={sessionDetailLoading}
            isSessionPopoverOpen={isSessionPopoverOpen}
            setIsSessionPopoverOpen={setIsSessionPopoverOpen}
            summaryMeta={summaryMeta}
            intentData={intentData}
          />
          <PermissionBar
            grant={grant}
            projectName={resolvedProjectName}
            sessionId={activeSessionId}
            canManageAccess={Boolean(activeTeamId)}
            onChange={() => setPermissionPickerOpen(true)}
          />

          <div
            className="relative flex flex-1 min-h-0 flex-col overflow-y-auto space-y-4 bg-surface/50 px-4 py-4 md:px-6"
            onScroll={handleChatScroll}
          >
            {messagesLoading ? (
              [
                { side: "start", w: "w-52", h: "h-12" },
                { side: "end", w: "w-36", h: "h-10" },
                { side: "start", w: "w-64", h: "h-16" },
                { side: "end", w: "w-44", h: "h-12" },
                { side: "start", w: "w-40", h: "h-10" },
                { side: "end", w: "w-56", h: "h-14" },
                { side: "start", w: "w-48", h: "h-12" },
                { side: "end", w: "w-32", h: "h-10" },
                { side: "start", w: "w-56", h: "h-14" },
              ].map((row, index) => (
                <div
                  key={index}
                  className={cn("flex gap-2", row.side === "end" ? "justify-end" : "justify-start")}
                >
                  <Skeleton
                    className={cn(row.w, row.h, "rounded-lg", row.side === "end" ? "bg-chat-user/30" : "bg-surface-active")}
                  />
                </div>
              ))
            ) : messageItems.length > 0 ? (
              messageItems.map((item) => {
                if (item.type === "sandbox_progress" || item.type === "agent_progress") {
                  return (
                    <div key={item.key} className="flex justify-start" role="status" aria-live="polite">
                      <div className="max-w-[85%] rounded-lg bg-chat-assistant px-3 py-2 text-sm text-darkgrey">
                        {item.progress.message || "I’m working on your file now."}
                      </div>
                    </div>
                  );
                }
                if (item.type === "date") {
                  return (
                    <div key={item.key} className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-surface-active/60" />
                      <span className="text-[11px] text-mediumgrey text-balance">
                        {item.label}
                      </span>
                      <div className="h-px flex-1 bg-surface-active/60" />
                    </div>
                  );
                }
                const message = item.message;
                const role = getMessageRole(message);
                const isUser = role === "user";
                const content = getMessageContent(message);
                const createdAt = message?.created_at;
                const isLoading = message?.status === "loading";
                const isError = message?.status === "error";
                const messageType = String(
                  message?.message_type || "",
                ).toLowerCase();
                const metadata = message?.metadata || {};
                const contractAction = metadata?.contract_action || null;
                const requestMessageId =
                  metadata?.access_request?.request_message_id || message?.id;
                const fileAccessResolved =
                  metadata?.intent === "file_access_request" &&
                  messages.some(
                    (candidate) =>
                      String(candidate?.metadata?.retry_of_message_id || "") ===
                      String(requestMessageId || ""),
                  );
                if (
                  !isUser &&
                  contractAction &&
                  isContractInProgress(contractAction.status)
                ) {
                  return null;
                }
                if (messageType === "contract_ready") {
                  return (
                    <div
                      key={item.key}
                      className="w-full flex mt-2 justify-start"
                    >
                      <ContractReadyCard
                        title={metadata?.title}
                        description={metadata?.description}
                        onView={() => handleViewContract(metadata?.contract_id, metadata)}
                      />
                    </div>
                  );
                }
                if (messageType === "contract_failed") {
                  return (
                    <div
                      key={item.key}
                      className="w-full flex mt-2 justify-start"
                    >
                      <ContractFailedCard
                        title={metadata?.title}
                        description={
                          metadata?.description || metadata?.error || content
                        }
                        onRetry={() => sendMessage("regenerate")}
                      />
                    </div>
                  );
                }
                if (messageType === "contract_expired") {
                  return (
                    <div
                      key={item.key}
                      className="w-full flex mt-2 justify-start"
                    >
                      <ContractExpiredCard
                        title={metadata?.title}
                        description={metadata?.description || content}
                        onRetry={() =>
                          sendMessage("try generating the contract again")
                        }
                      />
                    </div>
                  );
                }
                return (
                  <div
                    key={item.key}
                    className={cn(
                      "w-full flex mt-2 space-x-3 max-w-[85%] sm:max-w-xs md:max-w-sm",
                      isUser ? "ml-auto justify-end" : "justify-start",
                    )}
                  >
                    <div className="flex flex-col gap-1">
                      <div
                        className={cn(
                          "relative h-full p-2 rounded-lg",
                          isUser
                            ? "bg-chat-user"
                            : "bg-chat-assistant",
                        )}
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-1.5 px-4 py-3">
                            <span className="h-1.5 w-1.5 rounded-full bg-chat-muted animate-pulse" />
                            <span className="h-1.5 w-1.5 rounded-full bg-chat-muted animate-pulse [animation-delay:120ms]" />
                            <span className="h-1.5 w-1.5 rounded-full bg-chat-muted animate-pulse [animation-delay:240ms]" />
                          </div>
                        ) : isUser ? (
                          <p className="float-left h-full text-sm min-w-20 pr-8 whitespace-pre-wrap mb-1 text-white pb-4">
                            {content}
                          </p>
                        ) : (
                          <ChatMessageContent
                            content={content}
                            message={message}
                            sessionId={activeSessionId}
                            projectId={activeSession?.project_id || selectedProjectId}
                            fileAccessResolved={fileAccessResolved}
                            className="float-left h-full min-w-20 pr-8 mb-1 text-chat-muted"
                          />
                        )}
                        {isError ? (
                          <button
                            type="button"
                            onClick={() =>
                              handleRetryMessage(
                                message?.prompt || message?.content,
                                message?.client_message_id,
                              )
                            }
                            className="mt-2 text-[11px] text-danger underline cursor-pointer"
                          >
                            Retry
                          </button>
                        ) : null}
                        {createdAt ? (
                          <span
                            className={cn(
                              "absolute right-1.5 bottom-1 text-xs leading-none",
                              isUser ? "text-white" : "text-chat-muted",
                            )}
                          >
                            {formatToReadableTime(createdAt)}
                          </span>
                        ) : null}
                        {!isUser ? (
                          <span>
                            <TriangleIcon
                              className="rotate-[60deg] absolute top-[-5px] -left-1.5"
                              color={COLORS.chatAssistant}
                            />
                          </span>
                        ) : (
                          <span>
                            <TriangleIcon
                              className="-rotate-[60deg] absolute -top-[5px] -right-1.5"
                              color={COLORS.chatUser}
                            />
                          </span>
                        )}
                      </div>
                      {!isUser && !isLoading && !isError ? (
                        <SaveActions
                          teamId={activeTeamId}
                          projectId={activeProject?.id}
                          message={message}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })
            ) : activeSessionId ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-3">
                <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center">
                  <ChatIcon
                    width={24}
                    height={24}
                    className="text-primary/40"
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-darkgrey">
                    No messages yet
                  </p>
                  <p className="text-xs text-grey">
                    Describe the contract you need to start drafting.
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center">
                  <ContractGenerateIcon
                    width={30}
                    height={30}
                    className="text-primary/50"
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-darkgrey">
                    Draft a contract with AI
                  </p>
                  <p className="text-xs text-grey max-w-xs text-pretty">
                    Start with a question. Choose a project later to narrow the context.
                  </p>
                </div>
              </div>
            )}
            <div ref={scrollRef} className="absolute bottom-0" />
          </div>

          <div className="border-t border-surface-border px-4 md:px-6 py-3 bg-white">
            <div className="flex gap-2 bg-surface border border-surface-active rounded-xl p-1.5 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/50 transition-colors">
              <textarea
                ref={messageInputRef}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 resize-none bg-transparent border-none p-2 text-sm focus:ring-0 text-darkgrey placeholder:text-grey"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={isInputDisabled}
                style={{ minHeight: "40px", maxHeight: "120px" }}
              />
              {pendingAssistant ? (
                <Button
                  className="rounded-lg size-10 p-0 flex items-center justify-center shrink-0 bg-danger/100 hover:bg-danger text-white"
                  onClick={handleStopGeneration}
                  aria-label="Stop generation"
                  title="Stop generation"
                  icon={StopIcon}
                  iconSize={14}
                />
              ) : (
                <Button
                  className={cn(
                    "rounded-lg size-10 p-0 flex items-center justify-center shrink-0",
                    !messageInput.trim()
                      ? "bg-surface-hover text-mediumgrey"
                      : "bg-primary text-white",
                  )}
                  onClick={handleSend}
                  disabled={isInputDisabled || !messageInput.trim()}
                  aria-label="Send message"
                  title="Send message"
                  icon={SendIcon}
                  iconSize={16}
                />
              )}
            </div>
            <div className="px-1 mt-1.5 flex justify-end items-center text-[11px] text-darkgrey">
              {pendingAssistant ? (
                <span className="text-warning">
                  Generating response... Click stop to cancel.
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <ContractPanel
          contracts={contracts}
          selectedContractId={selectedContractId}
          contractDetail={contractDetail}
          contractLoading={contractLoading}
        />
        <PermissionPicker
          isOpen={Boolean(permissionPickerOpen)}
          onClose={() => setPermissionPickerOpen(false)}
          sessionId={activeSessionId}
          project={activeProject}
          initialGrant={grant}
        />
      </div>
    </div>
  );
};

const DraftFlow = () => (
  <DraftFlowProvider>
    <DraftFlowContent />
  </DraftFlowProvider>
);

export default DraftFlow;
